import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Play, ArrowDown, MessageSquarePlus } from "lucide-react";
import { api } from "../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * /stories — Instagram-style full-screen scroll-reel of approved video
 * testimonials.  Each testimonial with a `video_url` becomes a slide.
 * Uses CSS scroll-snap so the browser handles the paging.  The visible
 * video auto-plays (muted, per browser autoplay policy) via
 * IntersectionObserver; tapping a slide toggles play/pause + unmute.
 */
export default function Stories() {
  const [rows, setRows] = useState(null);
  const containerRef = useRef(null);
  const videoRefs = useRef({}); // id -> HTMLVideoElement
  const [activeId, setActiveId] = useState(null);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    api.get("/testimonials?limit=50")
      .then(({ data }) => setRows((data.testimonials || []).filter((t) => t.video_url)))
      .catch(() => setRows([]));
  }, []);

  // Watch which slide is currently in view; pause the others.
  useEffect(() => {
    if (!rows || rows.length === 0) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const id = e.target.getAttribute("data-story-id");
          const vid = videoRefs.current[id];
          if (!vid) continue;
          if (e.isIntersecting && e.intersectionRatio > 0.6) {
            setActiveId(id);
            vid.play().catch(() => {});
          } else {
            vid.pause();
          }
        }
      },
      { threshold: [0.6], root: containerRef.current },
    );
    Object.values(videoRefs.current).forEach((el) => el?.parentElement && io.observe(el.parentElement));
    return () => io.disconnect();
  }, [rows]);

  const toggleMute = () => {
    setMuted((m) => {
      const next = !m;
      Object.values(videoRefs.current).forEach((el) => { if (el) el.muted = next; });
      return next;
    });
  };

  if (rows === null) {
    return <div className="pt-32 text-center text-ink2" data-testid="stories-loading">Loading stories…</div>;
  }
  if (rows.length === 0) {
    return (
      <div className="pt-32 text-center text-ink2 max-w-md mx-auto px-4" data-testid="stories-empty">
        <h1 className="font-heading text-3xl mb-3">No video stories yet.</h1>
        <p className="mb-6">Once creators drop their 30-sec selfies, they show up here.  Be the first?</p>
        <Link to="/" className="btn-primary text-sm">
          <MessageSquarePlus className="w-4 h-4" /> Share your story on the home page
        </Link>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 top-16 bg-ink text-cream overflow-y-scroll snap-y snap-mandatory"
      data-testid="stories-reel"
      style={{ scrollSnapType: "y mandatory" }}
    >
      {/* Nav overlay */}
      <div className="fixed top-20 left-4 right-4 z-30 flex items-center justify-between pointer-events-none">
        <Link
          to="/"
          className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full border-2 border-cream/40 bg-ink/60 backdrop-blur px-3 py-1.5 text-xs uppercase tracking-widest font-bold hover:border-cream transition"
          data-testid="stories-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Home
        </Link>
        <button
          onClick={toggleMute}
          className="pointer-events-auto inline-flex items-center gap-1.5 rounded-full border-2 border-cream/40 bg-ink/60 backdrop-blur px-3 py-1.5 text-xs uppercase tracking-widest font-bold hover:border-cream transition"
          data-testid="stories-mute-toggle"
        >
          {muted ? "Unmute" : "Mute"}
        </button>
      </div>

      {rows.map((s, i) => (
        <section
          key={s.id}
          data-story-id={s.id}
          data-testid={`story-slide-${s.id}`}
          className="h-full min-h-[calc(100vh-4rem)] w-full snap-start flex items-center justify-center relative"
        >
          <video
            ref={(el) => { videoRefs.current[s.id] = el; }}
            src={`${BACKEND}${s.video_url}`}
            className="w-full h-full object-cover"
            loop
            playsInline
            muted={muted}
            preload={i < 3 ? "auto" : "metadata"}
            onClick={(e) => { const v = e.currentTarget; v.paused ? v.play() : v.pause(); }}
          />
          {/* Bottom caption overlay */}
          <div className="absolute inset-x-0 bottom-0 p-6 md:p-10 bg-gradient-to-t from-ink via-ink/70 to-transparent">
            <div className="max-w-2xl mx-auto">
              <div className="font-hand text-3xl md:text-4xl text-saffron leading-none mb-1" data-testid={`story-author-${s.id}`}>{s.author_name}</div>
              {s.role && <div className="text-[10px] uppercase tracking-widest font-bold text-cream/70 mb-3">{s.role}</div>}
              <p className="text-cream text-lg md:text-xl leading-relaxed mb-3">&ldquo;{s.body}&rdquo;</p>
              {s.project_name && <div className="text-xs text-marigold font-mono">→ {s.project_name}</div>}
            </div>
          </div>
          {/* Next-slide affordance */}
          {i < rows.length - 1 && (
            <div className="absolute bottom-4 right-4 text-cream/70 text-[10px] uppercase tracking-widest font-bold animate-bounce inline-flex items-center gap-1">
              Scroll <ArrowDown className="w-3 h-3" />
            </div>
          )}
          {/* Slide progress dots */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 flex gap-1" data-testid={`story-progress-${s.id}`}>
            {rows.map((_, j) => (
              <span key={j} className={`h-0.5 rounded-full transition-all ${j === i ? "w-8 bg-marigold" : "w-3 bg-cream/30"}`} />
            ))}
          </div>
          {/* Play icon overlay when paused */}
          {activeId !== s.id && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-16 h-16 rounded-full bg-cream/20 backdrop-blur border-2 border-cream/40 flex items-center justify-center">
                <Play className="w-8 h-8 text-cream" />
              </div>
            </div>
          )}
        </section>
      ))}
    </div>
  );
}
